import { useState, useEffect, useCallback } from "react";
import { toast, Toaster } from "sonner";
import { Mail, Copy, Check, LogOut, Inbox, RefreshCw, Send, X, Users, UserCheck, UserX, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getCookie(name) {
  const m = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return m ? decodeURIComponent(m[2]) : null;
}

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) throw new Error();
      onLogin();
    } catch {
      toast.error("Invalid credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#050505] px-6">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm rounded-2xl border border-[#1c1c1c] bg-[#0c0c0c] p-8"
      >
        <div className="mb-8 flex items-center gap-3">
          <span className="grid h-9 w-9 place-items-center border border-[#d91616]/60 text-[#d91616]">
            <span className="font-jp text-lg leading-none">藤</span>
          </span>
          <span className="font-display text-xl font-extrabold tracking-tight text-white">
            TOJI<span className="text-[#d91616]">.</span> Admin
          </span>
        </div>
        <label className="font-mono2 mb-2 block text-[10px] uppercase tracking-[0.2em] text-white/40">
          Email
        </label>
        <input
          data-testid="admin-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mb-5 w-full border-b border-[#2a2a2a] bg-transparent py-3 font-body text-white outline-none focus:border-[#d91616]"
          placeholder="admin@tojipep.com"
        />
        <label className="font-mono2 mb-2 block text-[10px] uppercase tracking-[0.2em] text-white/40">
          Password
        </label>
        <input
          data-testid="admin-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mb-8 w-full border-b border-[#2a2a2a] bg-transparent py-3 font-body text-white outline-none focus:border-[#d91616]"
          placeholder="••••••••"
        />
        <button
          data-testid="admin-login-btn"
          type="submit"
          disabled={loading}
          className="w-full bg-[#d91616] py-3 font-mono2 text-xs uppercase tracking-[0.3em] text-white transition-colors hover:bg-[#7a0000] disabled:opacity-60"
        >
          {loading ? "Signing in..." : "Enter"}
        </button>
      </form>
    </div>
  );
}

function InquiryCard({ item, onToggle, onReply }) {
  const [copied, setCopied] = useState(false);
  const [composing, setComposing] = useState(false);
  const [sending, setSending] = useState(false);
  const [subject, setSubject] = useState(
    `Re: Your Toji Peptides inquiry${item.interest ? ` — ${item.interest}` : ""}`
  );
  const [message, setMessage] = useState(
    `Hi ${item.name},\n\nThank you for reaching out to Toji Peptides regarding ${
      item.interest || "our peptides"
    }.\n\n`
  );
  const date = new Date(item.timestamp).toLocaleString();

  const copyEmail = () => {
    navigator.clipboard.writeText(item.email);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const send = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.error("Subject and message are required.");
      return;
    }
    setSending(true);
    const ok = await onReply(item, subject.trim(), message);
    setSending(false);
    if (ok) {
      setComposing(false);
      toast.success(`Reply sent to ${item.email}`);
    }
  };

  return (
    <div
      data-testid={`inquiry-${item.id}`}
      className={`rounded-xl border p-6 transition-colors ${
        item.handled ? "border-[#141414] bg-[#080808] opacity-70" : "border-[#1c1c1c] bg-[#0c0c0c]"
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h3 className="font-display text-xl font-extrabold tracking-tight text-white">
              {item.name}
            </h3>
            {item.interest && (
              <span className="font-mono2 border border-[#d91616]/40 px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] text-[#d91616]">
                {item.interest}
              </span>
            )}
            {item.handled && (
              <span className="font-mono2 text-[9px] uppercase tracking-[0.15em] text-green-500">
                ✓ Handled
              </span>
            )}
          </div>
          <p className="font-mono2 mt-1 text-xs text-white/40">{item.email} · {date}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setComposing((c) => !c)}
            data-testid={`reply-${item.id}`}
            className={`flex items-center gap-1.5 border px-3 py-2 font-mono2 text-[10px] uppercase tracking-[0.15em] transition-colors ${
              composing
                ? "border-[#d91616] bg-[#d91616] text-white"
                : "border-[#2a2a2a] text-white/80 hover:border-[#d91616] hover:text-[#d91616]"
            }`}
          >
            <Mail size={13} /> Reply
          </button>
          <button
            onClick={copyEmail}
            data-testid={`copy-${item.id}`}
            className="flex items-center gap-1.5 border border-[#2a2a2a] px-3 py-2 font-mono2 text-[10px] uppercase tracking-[0.15em] text-white/80 transition-colors hover:border-white"
          >
            {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? "Copied" : "Email"}
          </button>
          <button
            onClick={() => onToggle(item)}
            data-testid={`toggle-${item.id}`}
            className={`border px-3 py-2 font-mono2 text-[10px] uppercase tracking-[0.15em] transition-colors ${
              item.handled
                ? "border-[#2a2a2a] text-white/60 hover:border-white"
                : "border-[#d91616] bg-[#d91616] text-white hover:bg-[#7a0000]"
            }`}
          >
            {item.handled ? "Reopen" : "Mark done"}
          </button>
        </div>
      </div>
      {item.message && (
        <p className="mt-4 border-t border-[#161616] pt-4 font-body text-sm leading-relaxed text-white/70">
          {item.message}
        </p>
      )}

      {composing && (
        <div
          data-testid={`composer-${item.id}`}
          className="mt-4 rounded-lg border border-[#d91616]/25 bg-[#080808] p-4"
        >
          <div className="mb-2 flex items-center justify-between">
            <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#d91616]">
              Reply to {item.name}
            </span>
            <button
              onClick={() => setComposing(false)}
              data-testid={`composer-close-${item.id}`}
              className="text-white/40 transition-colors hover:text-white"
              aria-label="close composer"
            >
              <X size={15} />
            </button>
          </div>
          <input
            data-testid={`reply-subject-${item.id}`}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject"
            className="mb-2 w-full rounded border border-[#2a2a2a] bg-[#0c0c0c] px-3 py-2 font-body text-sm text-white outline-none focus:border-[#d91616]"
          />
          <textarea
            data-testid={`reply-message-${item.id}`}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={6}
            placeholder="Write your reply…"
            className="w-full resize-y rounded border border-[#2a2a2a] bg-[#0c0c0c] px-3 py-2 font-body text-sm leading-relaxed text-white outline-none focus:border-[#d91616]"
          />
          <div className="mt-3 flex items-center justify-end gap-2">
            <button
              onClick={() => setComposing(false)}
              data-testid={`reply-cancel-${item.id}`}
              className="border border-[#2a2a2a] px-4 py-2 font-mono2 text-[10px] uppercase tracking-[0.15em] text-white/60 transition-colors hover:border-white"
            >
              Cancel
            </button>
            <button
              onClick={send}
              disabled={sending}
              data-testid={`reply-send-${item.id}`}
              className="flex items-center gap-1.5 bg-[#d91616] px-5 py-2 font-mono2 text-[10px] uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#7a0000] disabled:opacity-60"
            >
              <Send size={13} /> {sending ? "Sending…" : "Send email"}
            </button>
          </div>
        </div>
      )}

      {item.replies && item.replies.length > 0 && (
        <div className="mt-4 space-y-2 border-t border-[#161616] pt-4">
          {item.replies.map((r, i) => (
            <div key={i} data-testid={`sent-reply-${item.id}-${i}`} className="rounded border border-[#161616] bg-[#0a0a0a] p-3">
              <div className="font-mono2 flex items-center gap-2 text-[9px] uppercase tracking-[0.15em] text-green-500">
                <Send size={11} /> Sent · {new Date(r.sent_at).toLocaleString()}
              </div>
              <p className="mt-1 font-body text-xs font-semibold text-white/80">{r.subject}</p>
              <p className="mt-1 whitespace-pre-line font-body text-xs leading-relaxed text-white/50">{r.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function statusBadge(status) {
  if (status === "approved")
    return { label: "✓ Approved", cls: "border-green-600/40 text-green-500" };
  if (status === "revoked")
    return { label: "✕ Revoked", cls: "border-[#d91616]/50 text-[#d91616]" };
  return { label: "• Pending", cls: "border-amber-500/40 text-amber-400" };
}

function MemberCard({ item, onApprove, onRevoke, defaultRevoking }) {
  const [revoking, setRevoking] = useState(!!defaultRevoking);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const date = new Date(item.created_at).toLocaleString();
  const badge = statusBadge(item.status);

  const approve = async () => {
    setBusy(true);
    await onApprove(item);
    setBusy(false);
  };
  const revoke = async () => {
    setBusy(true);
    const ok = await onRevoke(item, reason.trim());
    setBusy(false);
    if (ok) setRevoking(false);
  };

  return (
    <div
      data-testid={`member-${item.id}`}
      className={`rounded-xl border p-6 transition-colors ${
        item.status === "approved"
          ? "border-green-900/30 bg-[#080b08]"
          : item.status === "revoked"
          ? "border-[#1c1010] bg-[#0b0808] opacity-80"
          : "border-[#1c1c1c] bg-[#0c0c0c]"
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h3 className="font-display text-xl font-extrabold tracking-tight text-white">
              {item.name}
            </h3>
            <span
              data-testid={`member-status-${item.id}`}
              className={`font-mono2 border px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] ${badge.cls}`}
            >
              {badge.label}
            </span>
          </div>
          <p className="font-mono2 mt-1 text-xs text-white/40">
            {item.email} · {date}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {item.status !== "approved" && (
            <button
              onClick={approve}
              disabled={busy}
              data-testid={`member-approve-${item.id}`}
              className="flex items-center gap-1.5 border border-green-700/60 bg-green-700/20 px-3 py-2 font-mono2 text-[10px] uppercase tracking-[0.15em] text-green-400 transition-colors hover:bg-green-700/40 disabled:opacity-50"
            >
              <UserCheck size={13} /> Approve
            </button>
          )}
          {item.status !== "revoked" && (
            <button
              onClick={() => setRevoking((r) => !r)}
              data-testid={`member-revoke-toggle-${item.id}`}
              className={`flex items-center gap-1.5 border px-3 py-2 font-mono2 text-[10px] uppercase tracking-[0.15em] transition-colors ${
                revoking
                  ? "border-[#d91616] bg-[#d91616] text-white"
                  : "border-[#2a2a2a] text-white/70 hover:border-[#d91616] hover:text-[#d91616]"
              }`}
            >
              <UserX size={13} /> Revoke
            </button>
          )}
        </div>
      </div>

      {item.message && (
        <p className="mt-4 border-t border-[#161616] pt-4 font-body text-sm leading-relaxed text-white/70">
          {item.message}
        </p>
      )}

      {item.status === "revoked" && item.reason && (
        <p className="mt-4 flex items-start gap-2 border-t border-[#161616] pt-4 font-body text-xs leading-relaxed text-[#ff8a8a]">
          <Clock size={13} className="mt-0.5 shrink-0" /> Reason given: {item.reason}
        </p>
      )}

      {revoking && item.status !== "revoked" && (
        <div
          data-testid={`member-revoke-box-${item.id}`}
          className="mt-4 rounded-lg border border-[#d91616]/25 bg-[#080808] p-4"
        >
          <label className="font-mono2 mb-2 block text-[10px] uppercase tracking-[0.2em] text-[#d91616]">
            Explanation (shown to the applicant on login)
          </label>
          <textarea
            data-testid={`member-revoke-reason-${item.id}`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="e.g. We're unable to approve your request at this time."
            className="w-full resize-y rounded border border-[#2a2a2a] bg-[#0c0c0c] px-3 py-2 font-body text-sm leading-relaxed text-white outline-none focus:border-[#d91616]"
          />
          <div className="mt-3 flex items-center justify-end gap-2">
            <button
              onClick={() => setRevoking(false)}
              data-testid={`member-revoke-cancel-${item.id}`}
              className="border border-[#2a2a2a] px-4 py-2 font-mono2 text-[10px] uppercase tracking-[0.15em] text-white/60 transition-colors hover:border-white"
            >
              Cancel
            </button>
            <button
              onClick={revoke}
              disabled={busy}
              data-testid={`member-revoke-confirm-${item.id}`}
              className="flex items-center gap-1.5 bg-[#d91616] px-5 py-2 font-mono2 text-[10px] uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#7a0000] disabled:opacity-60"
            >
              <UserX size={13} /> {busy ? "Revoking…" : "Confirm revoke"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminInbox() {
  // authed: null = checking session, true = logged in, false = not logged in
  const [authed, setAuthed] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState("leads");
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [deepAction, setDeepAction] = useState(null); // {id, do}

  const logout = useCallback(async () => {
    try {
      await fetch(`${API}/admin/logout`, { method: "POST", credentials: "include" });
    } catch {
      /* ignore network errors on logout */
    }
    setAuthed(false);
    setItems([]);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/inquiries`, { credentials: "include" });
      if (res.status === 401) {
        setAuthed(false);
        setItems([]);
        return;
      }
      const data = await res.json();
      setItems(data);
    } catch {
      toast.error("Failed to load inquiries.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Check existing session on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/admin/me`, { credentials: "include" });
        if (!cancelled) setAuthed(res.ok);
      } catch {
        if (!cancelled) setAuthed(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load inquiries once authenticated
  useEffect(() => {
    if (authed === true) load();
  }, [authed, load]);

  const toggle = async (item) => {
    try {
      const res = await fetch(`${API}/admin/inquiries/${item.id}`, {
        method: "PATCH",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": getCookie("csrf_token") || "",
        },
        body: JSON.stringify({ handled: !item.handled }),
      });
      if (res.status === 401) return logout();
      if (!res.ok) throw new Error();
      setItems((prev) =>
        prev.map((i) => (i.id === item.id ? { ...i, handled: !i.handled } : i))
      );
    } catch {
      toast.error("Update failed.");
    }
  };

  const sendReply = async (item, subject, message) => {
    try {
      const res = await fetch(`${API}/admin/inquiries/${item.id}/reply`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": getCookie("csrf_token") || "",
        },
        body: JSON.stringify({ subject, message }),
      });
      if (res.status === 401) {
        logout();
        return false;
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "send failed");
      }
      const data = await res.json();
      setItems((prev) =>
        prev.map((i) =>
          i.id === item.id
            ? { ...i, handled: true, replies: [...(i.replies || []), data.reply] }
            : i
        )
      );
      return true;
    } catch (e) {
      toast.error(typeof e.message === "string" ? e.message : "Failed to send email.");
      return false;
    }
  };

  const loadMembers = useCallback(async () => {
    setMembersLoading(true);
    try {
      const res = await fetch(`${API}/admin/members`, { credentials: "include" });
      if (res.status === 401) {
        setAuthed(false);
        setMembers([]);
        return;
      }
      setMembers(await res.json());
    } catch {
      toast.error("Failed to load members.");
    } finally {
      setMembersLoading(false);
    }
  }, []);

  const approveMember = async (item) => {
    try {
      const res = await fetch(`${API}/admin/members/${item.id}/approve`, {
        method: "POST",
        credentials: "include",
        headers: { "X-CSRF-Token": getCookie("csrf_token") || "" },
      });
      if (res.status === 401) return logout();
      if (!res.ok) throw new Error();
      setMembers((prev) =>
        prev.map((m) => (m.id === item.id ? { ...m, status: "approved", reason: "" } : m))
      );
      toast.success(`${item.name} approved — they can now log in.`);
      return true;
    } catch {
      toast.error("Approve failed.");
      return false;
    }
  };

  const revokeMember = async (item, reason) => {
    try {
      const res = await fetch(`${API}/admin/members/${item.id}/revoke`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": getCookie("csrf_token") || "",
        },
        body: JSON.stringify({ reason }),
      });
      if (res.status === 401) {
        logout();
        return false;
      }
      if (!res.ok) throw new Error();
      setMembers((prev) =>
        prev.map((m) => (m.id === item.id ? { ...m, status: "revoked", reason } : m))
      );
      toast.success(`${item.name}'s access revoked.`);
      return true;
    } catch {
      toast.error("Revoke failed.");
      return false;
    }
  };

  // Load members once authenticated
  useEffect(() => {
    if (authed === true) loadMembers();
  }, [authed, loadMembers]);

  // Handle Telegram deep-link (?member=<id>&do=approve|revoke)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get("member");
    const doAction = params.get("do");
    if (id && doAction) {
      setView("members");
      setDeepAction({ id, do: doAction });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  // Auto-approve from deep link once members are loaded
  useEffect(() => {
    if (!deepAction || authed !== true || !members.length) return;
    const target = members.find((m) => m.id === deepAction.id);
    if (!target) {
      toast.error("Linked request not found.");
      setDeepAction(null);
      return;
    }
    if (deepAction.do === "approve" && target.status !== "approved") {
      approveMember(target);
      setDeepAction(null);
    }
    // for "revoke" we keep deepAction so the card opens its reason box
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepAction, authed, members]);

  if (authed === null) {
    return (
      <div
        data-testid="admin-checking"
        className="flex min-h-screen items-center justify-center bg-[#050505]"
      >
        <RefreshCw size={22} className="animate-spin text-[#d91616]" />
      </div>
    );
  }

  if (!authed) {
    return (
      <>
        <Toaster theme="dark" position="top-center" richColors />
        <Login onLogin={() => setAuthed(true)} />
      </>
    );
  }

  const filtered = items.filter((i) =>
    filter === "all" ? true : filter === "open" ? !i.handled : i.handled
  );
  const openCount = items.filter((i) => !i.handled).length;
  const pendingCount = members.filter((m) => m.status === "pending").length;

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Toaster theme="dark" position="top-center" richColors />
      <header className="sticky top-0 z-10 border-b border-[#141414] bg-black/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1100px] items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <span className="grid h-8 w-8 place-items-center border border-[#d91616]/60 text-[#d91616]">
              <Inbox size={15} />
            </span>
            <span className="font-display text-lg font-extrabold tracking-tight">
              Toji Admin
            </span>
            <div className="ml-2 flex items-center gap-1">
              <button
                onClick={() => setView("leads")}
                data-testid="view-leads"
                className={`flex items-center gap-1.5 px-3 py-1.5 font-mono2 text-[10px] uppercase tracking-[0.18em] transition-colors ${
                  view === "leads" ? "bg-[#d91616] text-white" : "border border-[#2a2a2a] text-white/60 hover:text-white"
                }`}
              >
                <Inbox size={12} /> Leads
                {openCount > 0 && <span className="text-[9px] opacity-80">{openCount}</span>}
              </button>
              <button
                onClick={() => setView("members")}
                data-testid="view-members"
                className={`flex items-center gap-1.5 px-3 py-1.5 font-mono2 text-[10px] uppercase tracking-[0.18em] transition-colors ${
                  view === "members" ? "bg-[#d91616] text-white" : "border border-[#2a2a2a] text-white/60 hover:text-white"
                }`}
              >
                <Users size={12} /> Members
                {pendingCount > 0 && <span className="text-[9px] opacity-80">{pendingCount}</span>}
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => (view === "leads" ? load() : loadMembers())}
              data-testid="refresh-btn"
              className="flex items-center gap-1.5 border border-[#2a2a2a] px-3 py-2 font-mono2 text-[10px] uppercase tracking-[0.15em] text-white/70 hover:border-white"
            >
              <RefreshCw size={13} className={loading || membersLoading ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="flex items-center gap-1.5 border border-[#2a2a2a] px-3 py-2 font-mono2 text-[10px] uppercase tracking-[0.15em] text-white/70 hover:border-[#d91616] hover:text-[#d91616]"
            >
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1100px] px-6 py-10">
        {view === "leads" ? (
          <>
            <div className="mb-6 flex gap-2">
              {["all", "open", "handled"].map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  data-testid={`filter-${f}`}
                  className={`font-mono2 px-4 py-2 text-[10px] uppercase tracking-[0.2em] transition-colors ${
                    filter === f ? "bg-[#d91616] text-white" : "border border-[#2a2a2a] text-white/60 hover:text-white"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>

            {filtered.length === 0 ? (
              <div className="rounded-xl border border-[#141414] bg-[#0a0a0a] py-24 text-center">
                <p className="font-mono2 text-sm uppercase tracking-[0.2em] text-white/40">
                  {loading ? "Loading..." : "No inquiries yet"}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filtered.map((item) => (
                  <InquiryCard key={item.id} item={item} onToggle={toggle} onReply={sendReply} />
                ))}
              </div>
            )}
          </>
        ) : (
          <div data-testid="members-list">
            {members.length === 0 ? (
              <div className="rounded-xl border border-[#141414] bg-[#0a0a0a] py-24 text-center">
                <p className="font-mono2 text-sm uppercase tracking-[0.2em] text-white/40">
                  {membersLoading ? "Loading..." : "No membership requests yet"}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {members.map((m) => (
                  <MemberCard
                    key={m.id}
                    item={m}
                    onApprove={approveMember}
                    onRevoke={revokeMember}
                    defaultRevoking={deepAction?.do === "revoke" && deepAction?.id === m.id}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
